<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { getUpdateMachineState } from '../../mealy/context';
    import IconUp from '../../icons/Up.svelte';
    import IconDown from '../../icons/Down.svelte';
    import IconStop from '../../icons/Stop.svelte';
    import * as F from 'fp-ts/function';


    import type { MealyExecutionState } from '../../state/executionState';
    import type { MachineStateMealy_Garagentor } from '../../state/machineState';
    import { mealyMachine_Garagentor_ensureMovement, mealyMachine_Garagentor_setPosition, mealyMachine_Garagentor_setPosition2, mealyMachine_Garagentor_stopExecutionWithError, mealyMachine_Garagentor_stopMovement, mealyMachine_executeSingleStep } from '../../actions/machine';
    import { EditMode } from '../../state/uiState';
    import Garage from './Garage.svelte';
    import Garagentor from './Garagentor.svelte';
    import Fernbedienung from './Fernbedienung.svelte';
    import FernbedienungTaster from './Fernbedienung_Taster.svelte';


    export let machineState: MachineStateMealy_Garagentor;
    export let mealyExecutionState: MealyExecutionState;
    export let isExecuting: boolean;
    
    const updateMachineState = getUpdateMachineState();

    const speed = 0.04;
    const positionMin = 0;
    const positionMax = 95;

    function onAnimationFrame() {
        animationFrameId = window.requestAnimationFrame(onAnimationFrame);

        if (machineState.uiState.editMode !== EditMode.EXECUTION && machineState.uiState.editMode !== EditMode.EXECUTION_TRANSITION) {
            return;
        }
        if (!machineState.garagentorSystemState.isMoving) {
            return;
        }

        const now = performance.now();
        const deltaS = speed * (now - machineState.garagentorSystemState.moveStartTimestamp);
        const newPosition = machineState.garagentorSystemState.direction === 'up'
                                ? machineState.garagentorSystemState.moveStartPosition + deltaS 
                                : machineState.garagentorSystemState.moveStartPosition - deltaS;
        if (newPosition <= positionMin) {
            updateMachineState(F.pipe(
                machineState,
                mealyMachine_Garagentor_setPosition2(positionMin),
                executeStep('Endschalter_unten'),
                checkMaxAndMinPosition
            ));
        } else if (newPosition >= positionMax) {
            updateMachineState(F.pipe(
                machineState,
                mealyMachine_Garagentor_setPosition2(positionMax),
                executeStep('Endschalter_oben'),
                checkMaxAndMinPosition
            ));
        } else {
            updateMachineState(mealyMachine_Garagentor_setPosition(machineState, newPosition));
        }
    }

    let animationFrameId = 0;

    onMount(() => {
        animationFrameId = window.requestAnimationFrame(onAnimationFrame);
    });

    onDestroy(() => {
        window.cancelAnimationFrame(animationFrameId);
    });

    function updateMovement(machineState: MachineStateMealy_Garagentor) {
        if (machineState.executionState.lastTransitionOutput === 'hoch') {
            return mealyMachine_Garagentor_ensureMovement(machineState, 'up')
        } else if (machineState.executionState.lastTransitionOutput === 'runter') {
            return mealyMachine_Garagentor_ensureMovement(machineState, 'down');
        } else if (machineState.executionState.lastTransitionOutput === 'stop') {
            return mealyMachine_Garagentor_stopMovement(machineState);
        }
        return machineState;
    }

    function checkMaxAndMinPosition(machineState: MachineStateMealy_Garagentor) {
        if (machineState.garagentorSystemState.position <= positionMin && machineState.garagentorSystemState.isMoving && machineState.garagentorSystemState.direction === 'down') {
            return mealyMachine_Garagentor_stopExecutionWithError(machineState, 'Das Garagentor ist unten angekommen und wurde bechädigt, weil es nicht angehalten wurde.');
        } else if (machineState.garagentorSystemState.position >= positionMax && machineState.garagentorSystemState.isMoving && machineState.garagentorSystemState.direction === 'up') {
            return mealyMachine_Garagentor_stopExecutionWithError(machineState, 'Das Garagentor ist oben angekommen und wurde bechädigt, weil es nicht angehalten wurde.');
        } else {
            return machineState;
        }
    }

    const executeStep = (inputChar: string) => (machineState: MachineStateMealy_Garagentor) => {
        const [newMachineState, stepHasBeenExecuted] = mealyMachine_executeSingleStep(machineState, inputChar);
        if (stepHasBeenExecuted) {
            return updateMovement(newMachineState);
        } else {
            return newMachineState;
        }
    }

    function handleHochClick() {
        if (machineState.uiState.editMode !== EditMode.EXECUTION) {
            return;
        }

        updateMachineState(F.pipe(
            machineState,
            executeStep('hoch'),
            checkMaxAndMinPosition
        ));
    }
1
    function handleStopClick() {
        if (machineState.uiState.editMode !== EditMode.EXECUTION) {
            return;
        }

        updateMachineState(F.pipe(
            machineState,
            executeStep('stop'),
            checkMaxAndMinPosition
        ));
    }

    function handleRunterClick() {
        if (machineState.uiState.editMode !== EditMode.EXECUTION) {
            return;
        }

        updateMachineState(F.pipe(
            machineState,
            executeStep('runter'),
            checkMaxAndMinPosition
        ));
    }

    function handleButtonClick() {
        if (machineState.uiState.editMode !== EditMode.EXECUTION) {
            return;
        }

        updateMachineState(F.pipe(
            machineState,
            executeStep('Taster'),
            checkMaxAndMinPosition
        ));
    }
</script>

<div>
    <div style="display: flex; flex-direction: horizontal">
        <svg viewBox="0 0 200 280" width="200px" >
            <defs>
                <clipPath id="clipGaragentor">
                <rect x="0" y="0" width="120" height="100" />
                </clipPath>
            </defs>

            <Garage />

            <g transform="translate(40 60)">
                <g clip-path=url(#clipGaragentor)>
                    <g transform="translate(0 {- machineState.garagentorSystemState.position})" >
                        <Garagentor />
                    </g>
                </g>
            </g>

            <g transform="translate(50 180)">
                <Fernbedienung />
                <foreignObject x="34" y="23" width="75" height="80">
                    <button style="margin: 0; padding: 0; background-color: #f4f4f4; line-height: 0px;" on:click={handleButtonClick}><FernbedienungTaster /></button>
                </foreignObject>
            </g>
        </svg>

        <!-- <div style="display: flex; flex-direction: column; justify-content: center; margin-left: 20px;">
            <div style="display: flex;  align-items: center;">
                <button 
                    class="automaten-simulator-iconButton" 
                    on:click={handleHochClick}
                >
                    <IconUp />
                </button>
                <span style="padding-left: 10px;">
                    hoch
                </span>
            </div>

            <div style="height: 10px;"></div>

            <div style="display: flex;  align-items: center;">
                <button
                    class="automaten-simulator-iconButton"
                    on:click={handleStopClick}
                >
                    <IconStop />
                </button>
                <span style="padding-left: 10px;">
                    stop
                </span>
            </div>

            <div style="height: 10px;"></div>

            <div style="display: flex;  align-items: center;">
                <button
                    class="automaten-simulator-iconButton"
                    on:click={handleRunterClick}
                >
                    <IconDown />
                </button>
                <span style="padding-left: 10px;">
                    runter
                </span>
            </div>
        </div> -->
    </div>
</div>